import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { authConfigured } from '../auth/config';
import { member } from '../lib/api';
import AuthPanel from '../components/AuthPanel';
import { Button } from '../ui/button';
import { Text } from '../ui/text';

type Status = 'idle' | 'redeeming' | 'redeemed' | 'error';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ready, idToken } = useAuth();
  const token = searchParams.get('token') ?? '';
  const teamId = searchParams.get('team') ?? '';

  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string>();
  const attempted = useRef(false);

  useEffect(() => {
    if (!ready) return;
    if (!idToken) return;
    if (!token || !teamId) return;
    if (attempted.current) return;
    attempted.current = true;
    setStatus('redeeming');
    member
      .acceptInvite({ teamId, token })
      .then(() => {
        setStatus('redeemed');
        navigate(`/teams/${encodeURIComponent(teamId)}`, { replace: true });
      })
      .catch((err: unknown) => {
        setStatus('error');
        setErrorMsg(err instanceof Error ? err.message : String(err));
      });
  }, [ready, idToken, token, teamId, navigate]);

  if (!token || !teamId) {
    return (
      <AuthPanel
        eyebrow="Invite"
        title="This invite link is invalid"
        body="Ask the team admin to send you a fresh invite."
      >
        <Link to="/" className="text-sm font-medium text-zinc-200 hover:underline">
          Back to home
        </Link>
      </AuthPanel>
    );
  }

  if (!authConfigured) {
    return (
      <AuthPanel
        eyebrow="Invite"
        title="Sign-in is not configured in this build"
        body="Set VITE_USER_POOL_ID and VITE_USER_POOL_CLIENT_ID to redeem invites."
      >
        <Link to="/" className="text-sm font-medium text-zinc-200 hover:underline">
          Back to home
        </Link>
      </AuthPanel>
    );
  }

  if (ready && !idToken) {
    const fromPath = `/invites/accept?token=${encodeURIComponent(token)}&team=${encodeURIComponent(teamId)}`;
    const search = new URLSearchParams({ from: fromPath }).toString();
    return <Navigate to={`/login?${search}`} replace state={{ from: fromPath }} />;
  }

  if (status === 'error') {
    return (
      <AuthPanel
        eyebrow="Invite"
        title="We couldn't redeem this invite"
        body={errorMsg ?? 'Please ask the team admin to issue a new invite.'}
      >
        <div className="flex gap-3">
          <Button
            color="dark/zinc"
            onClick={() => {
              attempted.current = false;
              setErrorMsg(undefined);
              setStatus('idle');
            }}
          >
            Try again
          </Button>
          <Link to="/me" className="self-center text-sm hover:underline">
            Go to My BaseballSTRM
          </Link>
        </div>
      </AuthPanel>
    );
  }

  return (
    <AuthPanel
      eyebrow="Invite"
      title="Joining the team…"
      body="Accepting your invite. You'll be redirected once you're in."
    >
      <Text>Hold tight.</Text>
    </AuthPanel>
  );
}
